/**
 * V167 — Manifest Landing Page (cold traffic)
 *
 * Inspired by Famous.AI's "AI is dead" ad campaign — pattern-interrupt
 * hook + category reframe + antithetical poetic copy + founder authority +
 * single CTA. No price. No feature grid. Pure narrative.
 *
 * Routes:  /manifest, /manifesto
 *
 * A/B variants rotated server-side via GET /api/manifest/variant.
 * Tracking via POST /api/manifest/{impression,conversion}.
 *
 * Supported languages: sk (default), en, de, pl, hu, cs.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Skull, Zap, Bot, Eye, Lock } from "lucide-react";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";

const LANGS = ["sk", "en", "de", "pl", "hu", "cs"];

// -------------------------- Copy bundle ----------------------------------
// Each variant has the SAME body — only the war-cry headline differs.
// The villain + body + CTA stay constant to isolate headline performance.
const COPY = {
  sk: {
    eyebrow: "Manifest pre majiteľov e-shopov",
    headlines: {
      A: { kicker: "SEO nástroje", strike: "sú mŕtve.", continuation: "Agenti, ktorí ich nahradia, už pracujú za teba." },
      B: { kicker: "Prestaň kupovať", strike: "dashboardy.", continuation: "Najmi si agentov, ktorí robia prácu." },
      C: { kicker: "Tvoj e-shop nepotrebuje", strike: "ďalší nástroj.", continuation: "Potrebuje zamestnancov, ktorí nespia." },
    },
    villain_title: "Pravda, ktorú ti predajcovia SEO nepovedia",
    villain_lines: [
      "Ahrefs ti ukáže problém.",
      "SurferSEO ti dá graf.",
      "RankPill ti pošle report.",
      "Ale ani jeden z nich tú prácu neurobí.",
      "Ty si stále tá osoba, ktorá musí kliknúť.",
      "Ty si stále tá osoba, ktorá musí písať popisy.",
      "Ty si stále tá osoba, ktorá zaspáva o tretej ráno.",
    ],
    fork_title: "Tu sa cesty rozchádzajú.",
    fork_lines: [
      { l: "Staré nástroje", r: "Reportujú." },
      { l: "Naši agenti", r: "Exekútujú." },
      { l: "Staré nástroje", r: "Ukazujú dáta." },
      { l: "Naši agenti", r: "Píšu popisy, odpovedajú emaily, generujú obsah." },
      { l: "Staré nástroje", r: "Sú open 9-17." },
      { l: "Naši agenti", r: "Pracujú aj keď spíš." },
    ],
    pillars_title: "Tri veci, ktoré agenti robia inak",
    pillars: [
      { Icon: Eye, title: "Vidia tvoj e-shop", text: "Skenujú produkty, opisy, ceny, konverzie. V reálnom čase." },
      { Icon: Bot, title: "Robia rozhodnutia", text: "Bez teba. SEO autopilot prepíše titulok. Helpdesk odpovie zákazníkovi." },
      { Icon: Zap, title: "Učia sa z výsledkov", text: "Každú noc vyhodnotia čo fungovalo a zmenia stratégiu na ráno." },
    ],
    closer_title: "Toto nie je nástroj.",
    closer_lines: [
      "Toto je nová generácia.",
      "Žiadne kliknutia. Žiadne reporty. Žiadne čakanie.",
      "Iba výsledky, ktoré sa stávajú zatiaľ čo pracuješ na niečom dôležitejšom.",
      "Otázka nie je či. Otázka je kedy.",
      "A kto bude prvý.",
    ],
    cta_button: "Naskenuj môj e-shop teraz",
    cta_sub: "60 sekúnd · Žiadna registrácia · Žiadna kreditka",
    badge: "Diagnostika zadarmo",
    founder_note: "— Mika, zakladateľ TopRankPulse",
  },
  en: {
    eyebrow: "A manifesto for e-commerce owners",
    headlines: {
      A: { kicker: "SEO tools", strike: "are dead.", continuation: "The agents replacing them are already working for you." },
      B: { kicker: "Stop buying", strike: "dashboards.", continuation: "Hire agents that do the work." },
      C: { kicker: "Your store doesn't need", strike: "another tool.", continuation: "It needs employees that never sleep." },
    },
    villain_title: "The truth SEO vendors won't tell you",
    villain_lines: [
      "Ahrefs shows you the problem.",
      "SurferSEO draws you a graph.",
      "RankPill emails you a report.",
      "None of them do the work.",
      "You are still the person who has to click.",
      "You are still the person who has to write descriptions.",
      "You are still the person waking up at 3 AM.",
    ],
    fork_title: "Here the road splits.",
    fork_lines: [
      { l: "Old tools", r: "Report." },
      { l: "Our agents", r: "Execute." },
      { l: "Old tools", r: "Show you data." },
      { l: "Our agents", r: "Write descriptions, reply to emails, generate content." },
      { l: "Old tools", r: "Open 9-5." },
      { l: "Our agents", r: "Working while you sleep." },
    ],
    pillars_title: "Three things agents do differently",
    pillars: [
      { Icon: Eye, title: "They see your store", text: "Scanning products, copy, prices, conversion. In real time." },
      { Icon: Bot, title: "They make decisions", text: "Without you. The SEO autopilot rewrites titles. Helpdesk replies to customers." },
      { Icon: Zap, title: "They learn from outcomes", text: "Every night they review what worked and adjust the strategy for tomorrow." },
    ],
    closer_title: "This is not a tool.",
    closer_lines: [
      "This is the next generation.",
      "No clicks. No reports. No waiting.",
      "Just results, happening while you work on what matters.",
      "The question is not if. The question is when.",
      "And who will be first.",
    ],
    cta_button: "Scan my store now",
    cta_sub: "60 seconds · No signup · No credit card",
    badge: "Free diagnostic",
    founder_note: "— Mika, founder of TopRankPulse",
  },
  de: {
    eyebrow: "Ein Manifest für E-Commerce-Inhaber",
    headlines: {
      A: { kicker: "SEO-Tools", strike: "sind tot.", continuation: "Die Agenten, die sie ersetzen, arbeiten bereits für dich." },
      B: { kicker: "Hör auf, Dashboards", strike: "zu kaufen.", continuation: "Stelle Agenten ein, die die Arbeit machen." },
      C: { kicker: "Dein Shop braucht kein", strike: "weiteres Tool.", continuation: "Er braucht Mitarbeiter, die niemals schlafen." },
    },
    villain_title: "Die Wahrheit, die SEO-Anbieter dir nicht sagen",
    villain_lines: [
      "Ahrefs zeigt dir das Problem.",
      "SurferSEO zeichnet dir einen Graphen.",
      "RankPill schickt dir einen Report.",
      "Keiner von ihnen macht die Arbeit.",
      "Du bist immer noch derjenige, der klicken muss.",
      "Du bist immer noch derjenige, der Beschreibungen schreibt.",
      "Du bist immer noch derjenige, der um 3 Uhr nachts aufwacht.",
    ],
    fork_title: "Hier teilt sich der Weg.",
    fork_lines: [
      { l: "Alte Tools", r: "Berichten." },
      { l: "Unsere Agenten", r: "Führen aus." },
      { l: "Alte Tools", r: "Zeigen Daten." },
      { l: "Unsere Agenten", r: "Schreiben Texte, beantworten E-Mails, erstellen Inhalte." },
      { l: "Alte Tools", r: "Geöffnet 9-17 Uhr." },
      { l: "Unsere Agenten", r: "Arbeiten, während du schläfst." },
    ],
    pillars_title: "Drei Dinge, die Agenten anders machen",
    pillars: [
      { Icon: Eye, title: "Sie sehen deinen Shop", text: "Scannen Produkte, Texte, Preise, Conversion. In Echtzeit." },
      { Icon: Bot, title: "Sie treffen Entscheidungen", text: "Ohne dich. Der SEO-Autopilot schreibt Titel um. Helpdesk antwortet Kunden." },
      { Icon: Zap, title: "Sie lernen aus Ergebnissen", text: "Jede Nacht prüfen sie, was funktioniert hat, und passen die Strategie für morgen an." },
    ],
    closer_title: "Das ist kein Tool.",
    closer_lines: [
      "Das ist die nächste Generation.",
      "Keine Klicks. Keine Reports. Kein Warten.",
      "Nur Ergebnisse, die passieren, während du an etwas Wichtigerem arbeitest.",
      "Die Frage ist nicht ob. Die Frage ist wann.",
      "Und wer zuerst ist.",
    ],
    cta_button: "Meinen Shop jetzt scannen",
    cta_sub: "60 Sekunden · Keine Anmeldung · Keine Kreditkarte",
    badge: "Kostenlose Diagnose",
    founder_note: "— Mika, Gründer von TopRankPulse",
  },
  pl: {
    eyebrow: "Manifest dla właścicieli sklepów",
    headlines: {
      A: { kicker: "Narzędzia SEO", strike: "są martwe.", continuation: "Agenci, którzy je zastępują, już pracują dla ciebie." },
      B: { kicker: "Przestań kupować", strike: "dashboardy.", continuation: "Zatrudnij agentów, którzy wykonują pracę." },
      C: { kicker: "Twój sklep nie potrzebuje", strike: "kolejnego narzędzia.", continuation: "Potrzebuje pracowników, którzy nigdy nie śpią." },
    },
    villain_title: "Prawda, której sprzedawcy SEO ci nie powiedzą",
    villain_lines: [
      "Ahrefs pokazuje problem.",
      "SurferSEO rysuje wykres.",
      "RankPill wysyła raport.",
      "Żaden z nich nie wykonuje pracy.",
      "Nadal to ty musisz kliknąć.",
      "Nadal to ty piszesz opisy.",
      "Nadal to ty budzisz się o 3 nad ranem.",
    ],
    fork_title: "Tu drogi się rozchodzą.",
    fork_lines: [
      { l: "Stare narzędzia", r: "Raportują." },
      { l: "Nasi agenci", r: "Wykonują." },
      { l: "Stare narzędzia", r: "Pokazują dane." },
      { l: "Nasi agenci", r: "Piszą opisy, odpowiadają na maile, tworzą treści." },
      { l: "Stare narzędzia", r: "Otwarte 9-17." },
      { l: "Nasi agenci", r: "Pracują, kiedy śpisz." },
    ],
    pillars_title: "Trzy rzeczy, które agenci robią inaczej",
    pillars: [
      { Icon: Eye, title: "Widzą twój sklep", text: "Skanują produkty, opisy, ceny, konwersje. W czasie rzeczywistym." },
      { Icon: Bot, title: "Podejmują decyzje", text: "Bez ciebie. SEO autopilot przepisuje tytuły. Helpdesk odpowiada klientom." },
      { Icon: Zap, title: "Uczą się z wyników", text: "Każdej nocy sprawdzają, co zadziałało i dostosowują strategię na jutro." },
    ],
    closer_title: "To nie jest narzędzie.",
    closer_lines: [
      "To jest następna generacja.",
      "Bez klikania. Bez raportów. Bez czekania.",
      "Tylko wyniki, dziejące się gdy pracujesz nad czymś ważniejszym.",
      "Pytanie nie brzmi czy. Pytanie brzmi kiedy.",
      "I kto będzie pierwszy.",
    ],
    cta_button: "Zeskanuj mój sklep teraz",
    cta_sub: "60 sekund · Bez rejestracji · Bez karty",
    badge: "Bezpłatna diagnoza",
    founder_note: "— Mika, założyciel TopRankPulse",
  },
  hu: {
    eyebrow: "Kiáltvány webshop tulajdonosoknak",
    headlines: {
      A: { kicker: "Az SEO eszközök", strike: "meghaltak.", continuation: "Az ügynökök, akik leváltják őket, már neked dolgoznak." },
      B: { kicker: "Hagyd abba a", strike: "dashboardok vásárlását.", continuation: "Vegyél fel ügynököket, akik elvégzik a munkát." },
      C: { kicker: "A boltodnak nem kell", strike: "újabb eszköz.", continuation: "Olyan munkatársak kellenek, akik soha nem alszanak." },
    },
    villain_title: "Az igazság, amit az SEO eladók nem mondanak el",
    villain_lines: [
      "Az Ahrefs megmutatja a problémát.",
      "A SurferSEO grafikont rajzol.",
      "A RankPill jelentést küld.",
      "Egyikük sem végzi el a munkát.",
      "Még mindig te vagy az, akinek kattintania kell.",
      "Még mindig te írod a leírásokat.",
      "Még mindig te kelsz fel hajnali háromkor.",
    ],
    fork_title: "Itt szétválnak az utak.",
    fork_lines: [
      { l: "Régi eszközök", r: "Jelentenek." },
      { l: "Az ügynökeink", r: "Cselekszenek." },
      { l: "Régi eszközök", r: "Adatokat mutatnak." },
      { l: "Az ügynökeink", r: "Leírásokat írnak, emaileket válaszolnak, tartalmat generálnak." },
      { l: "Régi eszközök", r: "9-17 között nyitva." },
      { l: "Az ügynökeink", r: "Dolgoznak, miközben alszol." },
    ],
    pillars_title: "Három dolog, amit az ügynökök másképp csinálnak",
    pillars: [
      { Icon: Eye, title: "Látják a boltodat", text: "Termékeket, szövegeket, árakat, konverziót scannelnek. Valós időben." },
      { Icon: Bot, title: "Döntéseket hoznak", text: "Nélküled. Az SEO autopilot átírja a címeket. A helpdesk válaszol az ügyfeleknek." },
      { Icon: Zap, title: "Tanulnak az eredményekből", text: "Minden éjjel kiértékelik, mi működött, és módosítják a stratégiát holnapra." },
    ],
    closer_title: "Ez nem egy eszköz.",
    closer_lines: [
      "Ez a következő generáció.",
      "Nincs kattintás. Nincs jelentés. Nincs várakozás.",
      "Csak eredmények, miközben fontosabb dolgokon dolgozol.",
      "A kérdés nem az, hogy igen. A kérdés az, hogy mikor.",
      "És ki lesz az első.",
    ],
    cta_button: "Scanneljük le a boltomat most",
    cta_sub: "60 másodperc · Regisztráció nélkül · Bankkártya nélkül",
    badge: "Ingyenes diagnózis",
    founder_note: "— Mika, a TopRankPulse alapítója",
  },
  cs: {
    eyebrow: "Manifest pro majitele e-shopů",
    headlines: {
      A: { kicker: "SEO nástroje", strike: "jsou mrtvé.", continuation: "Agenti, kteří je nahrazují, už pracují za tebe." },
      B: { kicker: "Přestaň kupovat", strike: "dashboardy.", continuation: "Najmi si agenty, kteří dělají práci." },
      C: { kicker: "Tvůj e-shop nepotřebuje", strike: "další nástroj.", continuation: "Potřebuje zaměstnance, kteří nespí." },
    },
    villain_title: "Pravda, kterou ti prodejci SEO neřeknou",
    villain_lines: [
      "Ahrefs ti ukáže problém.",
      "SurferSEO ti dá graf.",
      "RankPill ti pošle report.",
      "Ani jeden z nich tu práci neudělá.",
      "Pořád jsi to ty, kdo musí kliknout.",
      "Pořád jsi to ty, kdo musí psát popisy.",
      "Pořád jsi to ty, kdo vstává ve 3 ráno.",
    ],
    fork_title: "Tady se cesty rozcházejí.",
    fork_lines: [
      { l: "Staré nástroje", r: "Reportují." },
      { l: "Naši agenti", r: "Vykonávají." },
      { l: "Staré nástroje", r: "Ukazují data." },
      { l: "Naši agenti", r: "Píšou popisy, odpovídají emaily, generují obsah." },
      { l: "Staré nástroje", r: "Otevřené 9-17." },
      { l: "Naši agenti", r: "Pracují, když spíš." },
    ],
    pillars_title: "Tři věci, které agenti dělají jinak",
    pillars: [
      { Icon: Eye, title: "Vidí tvůj e-shop", text: "Skenují produkty, popisy, ceny, konverze. V reálném čase." },
      { Icon: Bot, title: "Dělají rozhodnutí", text: "Bez tebe. SEO autopilot přepíše titulek. Helpdesk odpoví zákazníkovi." },
      { Icon: Zap, title: "Učí se z výsledků", text: "Každou noc vyhodnotí, co fungovalo, a změní strategii pro ráno." },
    ],
    closer_title: "Tohle není nástroj.",
    closer_lines: [
      "Tohle je nová generace.",
      "Žádné klikání. Žádné reporty. Žádné čekání.",
      "Jen výsledky, které se dějí, zatímco pracuješ na něčem důležitějším.",
      "Otázka není jestli. Otázka je kdy.",
      "A kdo bude první.",
    ],
    cta_button: "Naskenuj můj e-shop teď",
    cta_sub: "60 sekund · Bez registrace · Bez karty",
    badge: "Diagnostika zdarma",
    founder_note: "— Mika, zakladatel TopRankPulse",
  },
};

// Stable visitor ID so the same person sees the same variant across visits
function getVisitorId() {
  try {
    let v = localStorage.getItem("trp_visitor_id");
    if (!v) {
      v = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("trp_visitor_id", v);
    }
    return v;
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export default function ManifestPage() {
  const { language } = useLanguage();
  const lang = LANGS.includes(language) ? language : "en";
  const c = COPY[lang];

  const [variant, setVariant] = useState(null);
  const visitor = useMemo(getVisitorId, []);
  const impressionFiredRef = useRef(false);

  // Detect ad source from query string for tracking
  const source = useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get("src") || sp.get("utm_source") || "direct";
    } catch {
      return "direct";
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/manifest/variant`, { params: { visitor_id: visitor } });
        if (cancelled) return;
        const v = r.data?.variant || "A";
        setVariant(v);
        // Guard against double-fire (React StrictMode dev + axios retries)
        if (impressionFiredRef.current) return;
        impressionFiredRef.current = true;
        axios.post(`${API}/manifest/impression`, {
          variant: v, visitor_id: visitor, language: lang, source,
        }).catch(() => {});
      } catch {
        setVariant("A");
      }
    })();
    return () => { cancelled = true; };
  }, [visitor, lang, source]);

  const onCtaClick = () => {
    if (!variant) return;
    axios.post(`${API}/manifest/conversion`, {
      variant, visitor_id: visitor, language: lang, source,
    }).catch(() => {});
  };

  if (!variant) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white/40 text-sm font-mono">
        Loading manifesto…
      </div>
    );
  }

  const h = c.headlines[variant];

  return (
    <div className="min-h-screen bg-black text-white selection:bg-red-500/40">
      <ManifestoHeader lang={lang} />

      {/* HERO */}
      <section className="px-6 pt-16 sm:pt-24 pb-20 sm:pb-28 max-w-4xl mx-auto" data-testid="manifest-hero">
        <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
          <Skull className="w-3 h-3"/> {c.eyebrow}
        </div>
        <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-[0.95] mb-8" data-testid={`manifest-headline-${variant}`}>
          <span className="text-white">{h.kicker}</span>
          <br/>
          <span className="text-red-500">{h.strike}</span>
          <br/>
          <span className="text-white/70 text-3xl sm:text-5xl lg:text-6xl">{h.continuation}</span>
        </h1>
        <p className="text-white/40 text-sm font-mono mt-10">{c.founder_note}</p>
      </section>

      {/* VILLAIN */}
      <section className="border-t border-white/5 px-6 py-20 sm:py-28 max-w-3xl mx-auto" data-testid="manifest-villain">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-8 text-white/90">
          {c.villain_title}
        </h2>
        <div className="space-y-3 text-lg sm:text-2xl font-medium text-white/80 leading-snug">
          {c.villain_lines.map((line, i) => (
            <p
              key={i}
              className={i >= c.villain_lines.length - 3 ? "text-red-300" : ""}
            >
              {line}
            </p>
          ))}
        </div>
      </section>

      {/* FORK — antithetical pairs */}
      <section className="border-t border-white/5 px-6 py-20 sm:py-28 max-w-4xl mx-auto" data-testid="manifest-fork">
        <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-10 text-white/90">{c.fork_title}</h2>
        <div className="space-y-1">
          {c.fork_lines.map((row, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2 sm:gap-6 py-3 border-b border-white/5">
              <div className={`text-sm font-mono uppercase tracking-wider ${row.l.startsWith("Old") || row.l.startsWith("Staré") || row.l.startsWith("Stare") || row.l.startsWith("Régi") || row.l.startsWith("Alte") || row.l.startsWith("Az SEO") ? "text-white/30" : "text-emerald-300"}`}>
                {row.l}
              </div>
              <div className="text-xl sm:text-2xl font-bold text-white">{row.r}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="border-t border-white/5 px-6 py-20 sm:py-28 max-w-5xl mx-auto" data-testid="manifest-pillars">
        <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-10 text-white/90">{c.pillars_title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {c.pillars.map((p, i) => (
            <div key={i} className="border border-white/10 rounded-2xl p-6 bg-white/[0.02]">
              <p.Icon className="w-6 h-6 text-emerald-400 mb-4"/>
              <h3 className="text-xl font-black mb-2">{p.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CLOSER */}
      <section className="border-t border-white/5 px-6 py-20 sm:py-28 max-w-3xl mx-auto" data-testid="manifest-closer">
        <h2 className="text-4xl sm:text-6xl font-black tracking-tight leading-[1.05] mb-8 text-white">
          {c.closer_title}
        </h2>
        <div className="space-y-3 text-xl sm:text-2xl text-white/80 leading-snug font-medium mb-12">
          {c.closer_lines.map((line, i) => (
            <p key={i} className={i === c.closer_lines.length - 1 ? "text-red-400 font-black" : ""}>{line}</p>
          ))}
        </div>

        {/* CTA */}
        <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
          <Lock className="w-3 h-3"/> {c.badge}
        </div>
        <div className="space-y-3">
          <Link
            to={`/diagnostika-eshopu?src=manifest&v=${variant}`}
            onClick={onCtaClick}
            data-testid="manifest-cta"
            className="group inline-flex items-center justify-center gap-3 w-full sm:w-auto px-8 py-5 rounded-xl bg-white text-black font-black text-lg sm:text-xl hover:bg-red-400 hover:text-white transition-colors"
          >
            {c.cta_button}
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1"/>
          </Link>
          <p className="text-xs text-white/40">{c.cta_sub}</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 px-6 py-10 text-center text-white/30 text-xs font-mono">
        © TopRankPulse · Manifest v1 · variant {variant}
      </footer>
    </div>
  );
}

function ManifestoHeader({ lang }) {
  return (
    <header className="border-b border-white/5 px-6 py-4 flex items-center justify-between">
      <Link to="/" className="inline-flex items-center gap-2 text-white font-black text-base tracking-tight">
        <span className="text-red-400">▲</span> TopRankPulse
      </Link>
      <span className="text-[10px] font-mono uppercase tracking-widest text-white/30">{lang}</span>
    </header>
  );
}
